import { useState, useEffect } from "react";
import CustomButton from "../../components/CustomButton";
import { useTranslation } from "react-i18next";
import Modal from "../../components/Modal";
import classes from "../../components/Modal.module.css";
import ErrorMessage from "../../components/ErrorMessage";
import saveIcon from "../../assets/save_icon.png";
import CustomInput from "../../components/CustomInput";
import CustomInputWithUnit from "../../components/CustomInputWithUnit";
import { onlyNums } from "../../utils/OnlyNums";
import {
  updateDiscount,
  deleteDiscount,
} from "../../services/schoolDiscountsServices"; // Import the service
import Textarea from "../../components/Textarea";
import ErrorBox from "../../components/ErrorBox";
import Loader, { LOADER_SIZES } from "../../components/Loader";
import AreYouSureModal from "../../components/AreYouSureModal";
import deleteIcon from "../../assets/delete_Icon.png";
import closeIcon from "../../assets/close_icon.png";
import axiosClient from "../../services/axiosClient";

export default function UpdateEntitlementsModal({
  entitlement,
  onClose,
  onSuccess,
}) {
  const { t } = useTranslation("general");
  const [loadingModal, setLoadingModal] = useState(false);
  const [error, setError] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    price: 0,
    notes: "",
  });

  useEffect(() => {
    if (entitlement) {
      setFormData({
        name: entitlement.name || "",
        price: entitlement.price || 0,
        notes: entitlement.notes || "",
      });
    }
  }, [entitlement]);

  const handleCancel = () => {
    onClose();
  };

  const handleUpdateEntitlement = async () => {
    if (!formData.name.trim()) {
      setValidationError("noEntitlementName");
      return;
    }

    if (!formData.price || parseFloat(formData.price) <= 0) {
      setValidationError("invalidEntitlementValue");
      return;
    }

    try {
      setLoadingModal(true);
      setError(null);

      const response = await axiosClient.put(
        `/entitlements/updateEntitlement`,
        {
          id: entitlement.id,
          name: formData.name,
          price: formData.price,
          notes: formData.notes,
        }
      );

      if (response.status === 200) {
        onSuccess();
      }
    } catch (err) {
      console.error("Error updating entitlement:", err);
      setError("entitlementUpdateFailed");
    } finally {
      setLoadingModal(false);
    }
  };

  return (
    <>
      <Modal
        title="updateEntitlement"
        onClose={handleCancel}
        modalSize={"c-modal--small"}
        footer={
          <>
            {loadingModal ? <Loader size={LOADER_SIZES.MEDIUM} /> : null}
            {error ? (
              <ErrorMessage classList="o-row o-row--horizontal-align_end">
                {t(error)}
              </ErrorMessage>
            ) : null}
            {validationError ? (
              <ErrorMessage classList="o-row o-row--horizontal-align_end">
                {t(validationError)}
              </ErrorMessage>
            ) : null}
            <section className={classes.actions}>
              <CustomButton
                onClick={handleUpdateEntitlement}
                title={t("confirm")}
                icon={saveIcon}
                iconSize={16}
                disabled={loadingModal}
              />
              <CustomButton
                onClick={() => setShowDeleteModal(true)}
                title={t("delete")}
                icon={deleteIcon}
                iconSize={16}
                disabled={loadingModal}
                variant="red"
              />
              <CustomButton
                title={t("cancel")}
                onClick={handleCancel}
                variant="blank"
                icon={closeIcon}
                iconSize={16}
              />
            </section>
          </>
        }
      >
        {error ? (
          <ErrorBox classList="o-row o-row--horizontal-align_end">
            {t(error)}
          </ErrorBox>
        ) : null}
        {validationError ? (
          <ErrorBox classList="o-row o-row--horizontal-align_end">
            {t(validationError)}
          </ErrorBox>
        ) : null}
        <div className="o-column o-column--gap-size_400">
          <CustomInput
            title={t("entitlementName")}
            name="entitlementName"
            value={formData.name}
            onChange={(e) => {
              if (validationError === "noEntitlementName") {
                setValidationError(null);
              }
              setFormData((prev) => ({
                ...prev,
                name: e.target.value,
              }));
            }}
            flex={true}
            errorText={
              validationError === "noEntitlementName"
                ? t("noEntitlementName")
                : ""
            }
          />

          <CustomInputWithUnit
            name="price"
            title={t("price")}
            value={formData.price}
            onChange={(e) => {
              if (validationError === "invalidEntitlementValue") {
                setValidationError(null);
              }
              setFormData((prev) => ({
                ...prev,
                price: e.target.value,
              }));
            }}
            onKeyDown={onlyNums}
            flex={true}
            min={0}
            max={100}
            unit="IQD"
            type="moneyV2"
            errorText={
              validationError === "invalidEntitlementValue"
                ? t("invalidEntitlementValue")
                : ""
            }
          />

          <div className="">
            <label>{t("notes")}</label>
            <Textarea
              name="notes"
              value={formData.notes}
              onChange={(e) => {
                setFormData((prev) => ({
                  ...prev,
                  notes: e.target.value,
                }));
              }}
              flex={true}
            />
          </div>
        </div>
      </Modal>

      {showDeleteModal && (
        <DeleteModal
          setShowDeleteModal={setShowDeleteModal}
          entitlement={entitlement}
          onSuccess={() => {
            setShowDeleteModal(false);
            onSuccess();
          }}
        />
      )}
    </>
  );
}

function DeleteModal({ setShowDeleteModal, entitlement, onSuccess }) {
  const [loadingModal, setLoadingModal] = useState(false);
  const [error, setError] = useState("");
  const { t } = useTranslation("general");

  const handleDeleteEntitlement = async () => {
    try {
      setLoadingModal(true);
      setError(null);

      await axiosClient.delete(`/entitlements/deleteEntitlement`, {
        data: {
          id: entitlement.id,
        },
      });

      onSuccess();
    } catch (err) {
      console.error("Error deleting entitlement:", err);
      setError("entitlementDeleteFailed");
    } finally {
      setLoadingModal(false);
    }
  };
  return (
    <AreYouSureModal
      onClose={() => setShowDeleteModal(false)}
      onConfirm={handleDeleteEntitlement}
      customMessage="areYouSureToDeleteEntitlement"
      closeTitle="cancel"
      confirmTitle="delete"
      loading={loadingModal}
      confirmButtonVariant="red"
      error={t(error)}
    />
  );
}
